import type { RouteById, SuccessOf } from "@volgo/core/contract";
import { type FormEvent, useState } from "react";
import { useSession } from "../../session";
import { Button } from "../../ui/Button";
import { ConfirmDialog } from "../../ui/Dialog";
import { describeError } from "../../ui/errors";
import { formatDateTime } from "../../ui/format";
import { Loading } from "../../ui/States";
import { type Guild, OpsDrawer } from "./OpsDrawers";

/** The Discord bot (discord Spec 4.3, 6.4): the board's server list and the drawer that adds and registers. */

export type BotStatus = SuccessOf<RouteById<"admin.botStatus">>;

const GUILD_ID = /^[0-9]{17,20}$/;
const COMMAND_STATES: Record<Guild["commands"]["state"], { label: string; tone: string }> = {
  registered: { label: "명령 등록됨", tone: "ok" },
  failed: { label: "등록 실패", tone: "bad" },
  pending: { label: "등록 대기", tone: "idle" },
};
const BOT_ERRORS = {
  CONFLICT: "이미 추가된 서버입니다.",
  UNAVAILABLE: "봇 설정이 없어 할 수 없습니다. API의 SHARE_BOT_* 환경 변수를 확인하세요.",
  NOT_FOUND: "이미 빠진 서버입니다. 화면을 새로 고쳐 확인하세요.",
};
const nameOf = (g: Guild) => g.note || `서버 ${g.guildId}`;

export function BotSection({
  bot,
  error,
  onOpen,
}: {
  bot: BotStatus | null;
  error: unknown;
  onOpen: () => void;
}) {
  return (
    <section className="ops-w" aria-label="봇">
      <header>
        <h2>봇</h2>
        {bot && (
          <span className="ops-r note">
            <span className="ops-dot" data-tone={bot.configured ? "ok" : "idle"} />{" "}
            {bot.configured ? "설정됨" : "봇 설정 없음"}
          </span>
        )}
      </header>
      {error !== null && (
        <p className="ops-in note note-warn" role="alert">
          {describeError(error)}
        </p>
      )}
      <ul className="ops-list">
        {bot?.guilds.map((g) => (
          <li key={g.guildId}>
            <button type="button" data-guild={g.guildId} onClick={onOpen}>
              <span className="ops-dot" data-tone={COMMAND_STATES[g.commands.state].tone} />
              <span>
                {nameOf(g)}
                <small>{g.guildId}</small>
              </span>
              <span className="ops-pill" data-tone={COMMAND_STATES[g.commands.state].tone}>
                {COMMAND_STATES[g.commands.state].label}
              </span>
            </button>
          </li>
        ))}
        <li>
          <button type="button" className="ops-add" onClick={onOpen}>
            <span aria-hidden="true">+</span>
            <span>서버 추가</span>
          </button>
        </li>
      </ul>
    </section>
  );
}

/**
 * The allowed Discord servers: add one (17–20 digits), register its commands again, take it out. Without the
 * bot's environment (`configured: false`) adding and registering are off and a notice says why.
 */
export function BotDrawer({
  bot,
  error,
  onBot,
  onChanged,
  onClose,
}: {
  bot: BotStatus | null;
  error: unknown;
  /** an answer the drawer got: shown at once */
  onBot: (bot: BotStatus) => void;
  /** the board loads the state again */
  onChanged: () => void;
  onClose: () => void;
}) {
  const { client } = useSession();
  const [guildId, setGuildId] = useState("");
  const [note, setNote] = useState("");
  const [idError, setIdError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);
  const [removing, setRemoving] = useState<Guild | null>(null);

  const putGuild = (b: BotStatus, guild: Guild) =>
    onBot({
      ...b,
      guilds: b.guilds.some((g) => g.guildId === guild.guildId)
        ? b.guilds.map((g) => (g.guildId === guild.guildId ? guild : g))
        : [...b.guilds, guild],
    });

  async function act(run: () => Promise<void>) {
    setBusy(true);
    setActionError(null);
    try {
      await run();
    } catch (e) {
      setActionError(e);
    } finally {
      setBusy(false);
      onChanged();
    }
  }
  function add(e: FormEvent) {
    e.preventDefault();
    if (!bot) return;
    const id = guildId.trim();
    if (!GUILD_ID.test(id)) return setIdError(true);
    setIdError(false);
    void act(async () => {
      putGuild(bot, await client.call("admin.addBotGuild", { body: { guildId: id, note: note.trim() } }));
      setGuildId("");
      setNote("");
    });
  }
  const register = (b: BotStatus, guilds: readonly Guild[]) =>
    act(async () => {
      let current = b;
      for (const g of guilds) {
        const guild = await client.call("admin.registerBotCommands", { params: { guildId: g.guildId } });
        current = {
          ...current,
          guilds: current.guilds.map((x) => (x.guildId === guild.guildId ? guild : x)),
        };
        onBot(current);
      }
    });

  return (
    <OpsDrawer title="봇 서버" onClose={onClose}>
      {!bot && error === null && <Loading />}
      {error !== null && (
        <p className="note note-warn" role="alert">
          {describeError(error)}
        </p>
      )}
      {bot && (
        <>
          {bot.configured ? (
            <div className="ops-bot">
              <p>
                <span className="ops-dot" data-tone="ok" /> 봇 설정됨
              </p>
              <p className="note">앱 {bot.applicationId} · 상호작용 주소 /api/discord/interactions</p>
              <div className="ops-add-row">
                <a
                  className="btn"
                  href={`https://discord.com/oauth2/authorize?client_id=${bot.applicationId}&scope=applications.commands`}
                  target="_blank"
                  rel="noreferrer"
                >
                  초대 주소
                </a>
                <Button
                  disabled={busy || bot.guilds.length === 0}
                  onClick={() => void register(bot, bot.guilds)}
                >
                  모든 서버 다시 등록
                </Button>
              </div>
            </div>
          ) : (
            <p className="ops-memo ops-memo-warn" role="note">
              봇 설정 없음 · API에 SHARE_BOT_APPLICATION_ID·SHARE_BOT_PUBLIC_KEY·SHARE_BOT_TOKEN을 넣으면
              켜집니다. 그 전에는 서버를 추가하거나 명령을 등록할 수 없습니다.
            </p>
          )}
          {actionError !== null && (
            <p className="note note-warn" role="alert">
              {describeError(actionError, BOT_ERRORS)}
            </p>
          )}
          <ul className="ops-guilds" aria-label="허용한 서버">
            {bot.guilds.map((g) => {
              const state = COMMAND_STATES[g.commands.state];
              return (
                <li key={g.guildId} className="ops-guild" data-guild={g.guildId}>
                  <div className="ops-guild-name">
                    <b>{nameOf(g)}</b>
                    <code>{g.guildId}</code>
                    <span className="ops-pill" data-tone={state.tone}>
                      {state.label}
                      {g.commands.at ? ` · ${formatDateTime(g.commands.at)}` : ""}
                    </span>
                    {g.commands.error && <p className="note note-warn">{g.commands.error}</p>}
                  </div>
                  <Button
                    variant={g.commands.state === "failed" ? "primary" : "plain"}
                    aria-label={`${nameOf(g)} 명령 다시 등록`}
                    disabled={busy || !bot.configured}
                    onClick={() => void register(bot, [g])}
                  >
                    다시 등록
                  </Button>
                  <Button aria-label={`${nameOf(g)} 빼기`} disabled={busy} onClick={() => setRemoving(g)}>
                    빼기
                  </Button>
                </li>
              );
            })}
          </ul>
          <form className="ops-guild-add" onSubmit={add} aria-label="서버 추가">
            <label className="field">
              서버(길드) ID
              <input
                className="input"
                value={guildId}
                onChange={(e) => {
                  setGuildId(e.target.value);
                  setIdError(false);
                }}
                inputMode="numeric"
                placeholder="17~20자리 숫자"
                maxLength={20}
                autoComplete="off"
                disabled={!bot.configured}
                aria-invalid={idError}
              />
            </label>
            <label className="field">
              메모(선택)
              <input
                className="input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={100}
                disabled={!bot.configured}
              />
            </label>
            <Button type="submit" variant="primary" disabled={busy || !bot.configured || !guildId.trim()}>
              추가
            </Button>
          </form>
          {idError && (
            <p className="note note-warn" role="alert">
              서버 ID는 17~20자리 숫자입니다.
            </p>
          )}
          <p className="note">
            추가하면 그 서버에만 /검색·/정보를 등록합니다. 봇은 그 서버의 연령 제한 채널에서만 답합니다. 뺀
            서버에서 이미 만든 읽기 링크는 24시간 동안 그대로 열립니다. 초대는 applications.commands 권한만
            요청하며 봇을 서버 멤버로 넣을 필요는 없습니다.
          </p>
        </>
      )}
      {bot && removing && (
        <ConfirmDialog
          title={`‘${nameOf(removing)}’ 서버를 뺄까요?`}
          confirmLabel="빼기"
          onCancel={() => setRemoving(null)}
          returnFocus={() => document.querySelector<HTMLElement>(".ops-drawer")}
          onConfirm={() => {
            const g = removing;
            setRemoving(null);
            void act(async () => {
              await client.call("admin.removeBotGuild", { params: { guildId: g.guildId } });
              onBot({ ...bot, guilds: bot.guilds.filter((x) => x.guildId !== g.guildId) });
            });
          }}
        >
          <p>
            그 서버의 /검색·/정보 명령을 해제하고 허용 목록에서 뺍니다. 이미 만든 읽기 링크는 만료될 때까지
            열립니다.
          </p>
        </ConfirmDialog>
      )}
    </OpsDrawer>
  );
}
